import { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
	PlusIcon,
	PencilIcon,
	TrashIcon,
	ShieldCheckIcon,
	UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Button, ConfirmDialog, Modal, Spinner } from '../../components/ui';
import { rolesApi, ApiError } from '../../api';
import type {
	PermissionCatalog,
	Role,
	RolePayload,
	RolePermissionGroup,
	RoleStatus,
	RoleType,
} from '../../types/roles';

// ─── Type-badge styling (mirrors htmls/Salon Owner/17_settings_16.html) ──────────

const TYPE_BADGE_STYLES: Record<RoleType, string> = {
	admin: 'bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700 dark:from-amber-900/40 dark:to-amber-900/60 dark:text-amber-300',
	manager:
		'bg-gradient-to-br from-violet-100 to-violet-200 text-violet-700 dark:from-violet-900/40 dark:to-violet-900/60 dark:text-violet-300',
	stylist:
		'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700 dark:from-emerald-900/40 dark:to-emerald-900/60 dark:text-emerald-300',
	receptionist:
		'bg-gradient-to-br from-pink-100 to-pink-200 text-pink-700 dark:from-pink-900/40 dark:to-pink-900/60 dark:text-pink-300',
	custom: 'bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 dark:from-blue-900/40 dark:to-blue-900/60 dark:text-blue-300',
};

const STATUS_OPTIONS: { value: RoleStatus; label: string }[] = [
	{ value: 'active', label: 'Active' },
	{ value: 'inactive', label: 'Inactive' },
];

// ─── Role Card ───────────────────────────────────────────────────────

const RoleCard = ({
	role,
	onEdit,
	onDelete,
}: {
	role: Role;
	onEdit: (r: Role) => void;
	onDelete: (r: Role) => void;
}) => (
	<div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 transition-all hover:border-[#667eea] hover:shadow-[0_4px_12px_rgba(102,126,234,0.15)]">
		<div className="flex justify-between items-start mb-3 gap-3">
			<div className="min-w-0">
				<p className="text-base font-semibold text-gray-900 dark:text-white truncate">
					{role.name}
				</p>
				{role.description && (
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
						{role.description}
					</p>
				)}
			</div>
			<span
				className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold capitalize whitespace-nowrap ${TYPE_BADGE_STYLES[role.type]}`}
			>
				{role.type}
			</span>
		</div>

		<div className="flex items-center gap-2 mb-3">
			<span
				className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-bold text-white"
				style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
			>
				{role.permissionCount}
			</span>
			<span className="text-xs text-gray-500 dark:text-gray-400">permissions</span>
			{role.systemRole && (
				<span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
					System
				</span>
			)}
		</div>

		<div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
			<button
				onClick={() => onEdit(role)}
				className="cursor-pointer flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 transition-colors"
			>
				<PencilIcon className="w-4 h-4" />
				{role.systemRole ? 'View' : 'Edit'}
			</button>
			{!role.systemRole && (
				<button
					onClick={() => onDelete(role)}
					className="cursor-pointer flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 transition-colors"
				>
					<TrashIcon className="w-4 h-4" />
					Delete
				</button>
			)}
		</div>
	</div>
);

// ─── Role Form Modal (Add + Edit) ────────────────────────────────────

interface RoleModalProps {
	isOpen: boolean;
	mode: 'add' | 'edit';
	role: Role | null;
	catalog: PermissionCatalog | null;
	onClose: () => void;
	onSubmit: (payload: RolePayload, id: number | null) => Promise<void>;
}

const RoleModal = ({ isOpen, mode, role, catalog, onClose, onSubmit }: RoleModalProps) => {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [type, setType] = useState<RoleType>('custom');
	const [status, setStatus] = useState<RoleStatus>('active');
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [isSaving, setIsSaving] = useState(false);
	// Track whether the most recent permission change came from the user (vs. a
	// preset switch). When a user toggles manually, we flip type → 'custom'.
	const [userTouchedPermissions, setUserTouchedPermissions] = useState(false);

	const isReadOnly = mode === 'edit' && role?.systemRole === true;

	// Reset state when modal opens
	useEffect(() => {
		if (!isOpen) return;

		if (mode === 'edit' && role) {
			// Optional fields come back null from the API for roles saved without
			// them; coerce here so the trim()s below never see a null.
			setName(role.name ?? '');
			setDescription(role.description ?? '');
			setType(role.type ?? 'custom');
			setStatus(role.status ?? 'active');
			const perms = new Set<string>();
			(role.permissionGroups ?? []).forEach(g =>
				(g.permissions ?? []).forEach(p => perms.add(p.name))
			);
			setSelected(perms);
		} else {
			setName('');
			setDescription('');
			setType('custom');
			setStatus('active');
			setSelected(new Set());
		}
		setUserTouchedPermissions(false);
		setIsSaving(false);
	}, [isOpen, mode, role]);

	// Selecting a preset: replace permission set with the preset's permissions
	const handlePresetChange = useCallback(
		(nextType: RoleType) => {
			setType(nextType);
			setUserTouchedPermissions(false);
			if (nextType === 'custom' || !catalog) return;
			const preset = catalog.presets.find(p => p.type === nextType);
			setSelected(new Set(preset?.permissions ?? []));
		},
		[catalog]
	);

	// Manually toggling a permission flips the type to 'custom'
	const togglePermission = useCallback(
		(name: string) => {
			if (isReadOnly) return;
			setSelected(prev => {
				const next = new Set(prev);
				if (next.has(name)) next.delete(name);
				else next.add(name);
				return next;
			});
			if (!userTouchedPermissions) setUserTouchedPermissions(true);
			setType('custom');
		},
		[isReadOnly, userTouchedPermissions]
	);

	const canSave = (name ?? '').trim().length > 0 && !isSaving && !isReadOnly;

	const handleSave = async () => {
		if (!canSave) return;
		setIsSaving(true);
		try {
			await onSubmit(
				{
					name: (name ?? '').trim(),
					description: (description ?? '').trim(),
					type,
					status,
					permissions: Array.from(selected),
				},
				mode === 'edit' && role ? role.id : null
			);
		} finally {
			setIsSaving(false);
		}
	};

	const presetOptions = useMemo(() => {
		const fromCatalog = catalog?.presets.map(p => ({ value: p.type, label: p.label })) ?? [];
		const seen = new Set(fromCatalog.map(o => o.value));
		// Ensure 'custom' is always present at the top
		const opts: { value: RoleType; label: string }[] = [{ value: 'custom', label: 'Custom' }];
		fromCatalog.forEach(o => {
			if (o.value !== 'custom') opts.push(o);
		});
		// Add any fallback values we know about but the catalog didn't return
		(['admin', 'manager', 'stylist', 'receptionist'] as RoleType[]).forEach(t => {
			if (!seen.has(t) && !opts.find(o => o.value === t)) {
				opts.push({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) });
			}
		});
		return opts;
	}, [catalog]);

	const groups: RolePermissionGroup[] = catalog?.permissionGroups ?? [];

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={mode === 'add' ? 'Add Role' : isReadOnly ? 'Role Details' : 'Edit Role'}
			size="xl"
		>
			<div className="space-y-5">
				{/* Row 1: Name + Preset */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
							Role Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							value={name}
							onChange={e => setName(e.target.value)}
							placeholder="e.g., Manager"
							disabled={isReadOnly}
							className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
						/>
					</div>
					<div>
						<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
							Role Presets
						</label>
						<select
							value={type}
							onChange={e => handlePresetChange(e.target.value as RoleType)}
							disabled={isReadOnly}
							className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
						>
							{presetOptions.map(opt => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>
				</div>

				{/* Description */}
				<div>
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
						Description
					</label>
					<input
						type="text"
						value={description}
						onChange={e => setDescription(e.target.value)}
						placeholder="Brief description"
						disabled={isReadOnly}
						className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
					/>
				</div>

				{/* Permissions */}
				<div>
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
						Permissions
					</label>
					{groups.length === 0 ? (
						<div className="flex items-center justify-center py-6 text-sm text-gray-400 dark:text-gray-500">
							<Spinner size="sm" />
							<span className="ml-2">Loading permissions…</span>
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							{groups.map(group => (
								<div
									key={group.key}
									className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4"
								>
									<h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 pb-2 mb-3 border-b border-gray-200 dark:border-gray-600">
										{group.label}
									</h4>
									<div className="flex flex-col gap-1">
										{group.permissions.map(perm => (
											<label
												key={perm.name}
												className={`flex items-center gap-2.5 py-1 text-sm text-gray-600 dark:text-gray-300 ${isReadOnly ? 'cursor-not-allowed' : 'cursor-pointer'}`}
											>
												<input
													type="checkbox"
													checked={selected.has(perm.name)}
													onChange={() => togglePermission(perm.name)}
													disabled={isReadOnly}
													className="w-4 h-4 rounded accent-[#667eea] cursor-pointer disabled:cursor-not-allowed"
												/>
												<span>{perm.label}</span>
											</label>
										))}
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Status */}
				<div>
					<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
						Status
					</label>
					<select
						value={status}
						onChange={e => setStatus(e.target.value as RoleStatus)}
						disabled={isReadOnly}
						className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
					>
						{STATUS_OPTIONS.map(opt => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>

				{/* Footer */}
				<div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
					<Button variant="secondary" onClick={onClose} disabled={isSaving}>
						{isReadOnly ? 'Close' : 'Cancel'}
					</Button>
					{!isReadOnly && (
						<Button
							variant="primary"
							onClick={handleSave}
							disabled={!canSave}
							isLoading={isSaving}
						>
							Save Role
						</Button>
					)}
				</div>
			</div>
		</Modal>
	);
};

// ─── Main Tab Component ──────────────────────────────────────────────

const SettingsRolesTab = () => {
	const [roles, setRoles] = useState<Role[]>([]);
	const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [modal, setModal] = useState<{
		isOpen: boolean;
		mode: 'add' | 'edit';
		role: Role | null;
	}>({ isOpen: false, mode: 'add', role: null });

	const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Fetch roles + catalog on mount (lazy: this component only mounts when tab is active)
	const loadData = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const [rolesData, catalogData] = await Promise.all([
				rolesApi.list(),
				rolesApi.catalog(),
			]);
			setRoles(rolesData);
			setCatalog(catalogData);
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to load roles');
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const handleAdd = () => setModal({ isOpen: true, mode: 'add', role: null });
	const handleEdit = (role: Role) => setModal({ isOpen: true, mode: 'edit', role });
	const handleCloseModal = () => setModal(m => ({ ...m, isOpen: false }));

	const handleSubmit = async (payload: RolePayload, id: number | null) => {
		try {
			if (id != null) {
				const updated = await rolesApi.update(id, payload);
				setRoles(prev => prev.map(r => (r.id === id ? updated : r)));
				toast.success('Role updated');
			} else {
				const created = await rolesApi.create(payload);
				setRoles(prev => [...prev, created]);
				toast.success('Role created');
			}
			handleCloseModal();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to save role');
			throw err;
		}
	};

	const handleConfirmDelete = async () => {
		if (!deleteTarget) return;
		setIsDeleting(true);
		try {
			await rolesApi.delete(deleteTarget.id);
			setRoles(prev => prev.filter(r => r.id !== deleteTarget.id));
			toast.success('Role deleted');
			setDeleteTarget(null);
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to delete role');
		} finally {
			setIsDeleting(false);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Spinner size="lg" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-20 gap-4">
				<p className="text-red-500 dark:text-red-400">{error}</p>
				<button
					onClick={loadData}
					className="px-4 py-2 rounded-lg font-semibold text-sm text-white"
					style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
				>
					Retry
				</button>
			</div>
		);
	}

	return (
		<>
			{/* Header */}
			<div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
				<div className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
					<ShieldCheckIcon className="w-5 h-5 text-[#667eea]" />
					Roles & Permissions
				</div>
				<Button variant="primary" onClick={handleAdd}>
					<PlusIcon className="w-4 h-4 mr-1" />
					Add Role
				</Button>
			</div>

			{/* Empty state */}
			{roles.length === 0 ? (
				<div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 py-16 text-center">
					<UserGroupIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
					<h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
						No roles yet
					</h3>
					<p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
						Create a role to manage what staff can see and do.
					</p>
					<Button variant="primary" onClick={handleAdd}>
						<PlusIcon className="w-4 h-4 mr-1" />
						Add Role
					</Button>
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
					{roles.map(role => (
						<RoleCard
							key={role.id}
							role={role}
							onEdit={handleEdit}
							onDelete={setDeleteTarget}
						/>
					))}
				</div>
			)}

			{/* Add/Edit Modal */}
			<RoleModal
				isOpen={modal.isOpen}
				mode={modal.mode}
				role={modal.role}
				catalog={catalog}
				onClose={handleCloseModal}
				onSubmit={handleSubmit}
			/>

			{/* Delete Confirm */}
			<ConfirmDialog
				isOpen={deleteTarget !== null}
				onClose={() => (isDeleting ? null : setDeleteTarget(null))}
				onConfirm={handleConfirmDelete}
				title="Delete Role"
				message={
					deleteTarget
						? `Are you sure you want to delete "${deleteTarget.name}"? Staff with this role will lose its permissions.`
						: ''
				}
				confirmLabel="Yes, Delete"
				cancelLabel="Cancel"
				variant="danger"
				isLoading={isDeleting}
			/>
		</>
	);
};

export default SettingsRolesTab;
